<template>
  <div class="mt-8">
    <div
      v-if="errorMessage"
      class="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
    >
      {{ errorMessage }}
    </div>

    <div class="mb-4 flex items-center justify-end">
      <button
        type="button"
        class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-[#2D8CFF] focus:outline-none active:bg-gray-100 active:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="loading || downloading"
        @click="downloadSessionsLog"
      >
        {{ downloading ? 'Preparing PDF...' : 'Download Logs' }}
      </button>
    </div>

    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Location</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Device / Browser / OS</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">I.P. Address</th>
            <th class="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Time</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr v-if="!loading && orderedSessions.length === 0">
            <td
              colspan="4"
              class="px-4 py-4 text-sm text-gray-500"
            >
              No login sessions found.
            </td>
          </tr>
          <tr
            v-for="(session, index) in orderedSessions"
            :key="session.sessionUid || index"
            class="hover:bg-gray-50"
          >
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ session.location }}
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              <div class="flex items-center gap-2">
                <component
                  :is="getDeviceIcon(session.deviceType)"
                  :class="getIconClass(session.deviceType)"
                  class="h-6 w-6"
                />
                <div class="min-w-0">
                  <div class="truncate">{{ session.device }}</div>
                  <div class="truncate text-xs text-gray-500">{{ session.browserName }} - {{ session.osName }}</div>
                </div>
              </div>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ session.ipAddress }}
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ session.time }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="totalPages > 1"
      class="mx-2 mb-6 flex items-center justify-between border-t py-5"
    >
      <div class="text-sm text-gray-700">
        Showing
        <span class="font-medium">{{ startIndex }}</span>
        to
        <span class="font-medium">{{ endIndex }}</span>
        of
        <span class="font-medium">{{ totalSessions }}</span>
        results
      </div>
      <div class="flex items-center gap-4">
        <div class="text-sm text-gray-700">
          Page
          <span class="font-medium">{{ currentPage }}</span>
          of
          <span class="font-medium">{{ totalPages }}</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-[10px] py-[6px] text-xs font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-[#2D8CFF] focus:outline-none active:bg-gray-100 active:text-gray-700"
            :disabled="loading || currentPage === 1"
            @click="prevPage"
          >
            Previous
          </button>
          <button
            class="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-[10px] py-[6px] text-xs font-medium text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-[#2D8CFF] focus:outline-none active:bg-gray-100 active:text-gray-700"
            :disabled="loading || currentPage >= totalPages"
            @click="nextPage"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, ref, watch } from 'vue';
import { ComputerDesktopIcon, DevicePhoneMobileIcon, DeviceTabletIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  memberId: {
    type: [Number, String],
    required: true,
  },
});

const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').trim().replace(/\/+$/, '');
const apiBackendUrl = String(process.env.VUE_APP_PLATFORMA_API_BACKEND_URL || '').trim().replace(/\/+$/, '');
const itemsPerPage = 10;

const loading = ref(false);
const downloading = ref(false);
const errorMessage = ref('');
const sessions = ref([]);
const currentPage = ref(1);
const totalSessions = ref(0);
const totalPages = ref(1);

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const normalizeRows = (rows) => (
  Array.isArray(rows)
    ? rows.map((row) => ({
      sessionUid: String(row?.sessionUid || ''),
      location: String(row?.locationLabel || 'Unknown location'),
      device: String(row?.deviceName || 'Unknown device'),
      browserName: String(row?.browserName || 'Unknown Browser'),
      osName: String(row?.osName || 'Unknown OS'),
      deviceType: String(row?.deviceType || '').toLowerCase(),
      ipAddress: String(row?.ipAddress || '-'),
      loginAt: row?.loginAt || null,
      time: formatDateTime(row?.loginAt),
    }))
    : []
);

const parsePayload = async (response) => {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) return response.json().catch(() => ({}));
  const text = await response.text().catch(() => '');
  return { __rawText: text };
};

const parseDownloadFilename = (contentDisposition, fallbackName) => {
  const header = String(contentDisposition || '');
  if (!header) return fallbackName;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(String(utf8Match[1]).replace(/^"|"$/g, ''));
    } catch (_error) {
      return fallbackName;
    }
  }

  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  if (asciiMatch?.[1]) return String(asciiMatch[1]).trim();
  return fallbackName;
};

const triggerDownload = (blob, fileName) => {
  if (!(blob instanceof Blob)) throw new Error('Invalid report file');
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = String(fileName || 'login-sessions-report.pdf');
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
};

const downloadSessionsLog = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) {
    errorMessage.value = 'Invalid member id';
    return;
  }

  downloading.value = true;
  errorMessage.value = '';
  try {
    const requestPaths = [
      `/api/members/${encodeURIComponent(String(memberId))}/sessions/download`,
      `/api/creators/members/${encodeURIComponent(String(memberId))}/sessions/download`,
    ];

    const requestBases = Array.from(new Set([
      apiProxyUrl,
      apiBackendUrl,
    ].map((value) => String(value || '').trim()).filter(Boolean)));

    let lastError = 'Failed to download login session report';
    for (const base of requestBases) {
      for (const path of requestPaths) {
        const response = await fetch(`${base}${path}`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const blob = await response.blob();
          if (!blob || !blob.size) {
            lastError = 'Received an empty report file';
            continue;
          }
          const fallbackName = `member-login-sessions-${memberId}.pdf`;
          const fileName = parseDownloadFilename(response.headers.get('content-disposition'), fallbackName);
          triggerDownload(blob, fileName);
          return;
        }

        const payload = await parsePayload(response);
        const rawText = String(payload?.__rawText || '').toLowerCase();
        const looksLikeRouteMiss = rawText.includes('<!doctype html') && rawText.includes('cannot get');
        if (looksLikeRouteMiss) {
          continue;
        }
        lastError = String(payload?.message || payload?.error || `Failed to download report (${response.status})`);
        if (response.status !== 404) {
          break;
        }
      }
    }

    throw new Error(lastError);
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed to download login session report');
  } finally {
    downloading.value = false;
  }
};

const fetchSessions = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) {
    sessions.value = [];
    totalSessions.value = 0;
    totalPages.value = 1;
    return;
  }

  loading.value = true;
  errorMessage.value = '';

  try {
    const query = new URLSearchParams({
      page: String(currentPage.value),
      limit: String(itemsPerPage),
    }).toString();

    const requestPaths = [
      `/api/members/${encodeURIComponent(String(memberId))}/sessions?${query}`,
      `/api/creators/members/${encodeURIComponent(String(memberId))}/sessions?${query}`,
    ];

    const requestBases = Array.from(new Set([
      apiProxyUrl,
      apiBackendUrl,
    ].map((value) => String(value || '').trim()).filter(Boolean)));

    let response = null;
    let payload = {};
    let lastError = 'Failed to load login sessions';

    for (const base of requestBases) {
      for (const path of requestPaths) {
        response = await fetch(`${base}${path}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        payload = await parsePayload(response);

        if (response.ok && payload?.success !== false) {
          break;
        }

        const rawText = String(payload?.__rawText || '').toLowerCase();
        const looksLikeRouteMiss = rawText.includes('<!doctype html') && rawText.includes('cannot get');
        if (looksLikeRouteMiss) {
          continue;
        }

        lastError = String(payload?.message || payload?.error || `Failed to load login sessions (${response.status})`);
        if (response.status !== 404) {
          break;
        }
      }
      if (response && response.ok && payload?.success !== false) {
        break;
      }
    }

    if (!response || !response.ok || payload?.success === false) {
      throw new Error(lastError);
    }

    sessions.value = normalizeRows(payload?.sessions || []);
    totalSessions.value = Number(payload?.pagination?.total || sessions.value.length || 0);
    totalPages.value = Math.max(1, Number(payload?.pagination?.totalPages || 1));
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed to load login sessions');
    sessions.value = [];
    totalSessions.value = 0;
    totalPages.value = 1;
  } finally {
    loading.value = false;
  }
};

const orderedSessions = computed(() => (
  [...sessions.value].sort((a, b) => {
    const aTs = a?.loginAt ? new Date(a.loginAt).getTime() : 0;
    const bTs = b?.loginAt ? new Date(b.loginAt).getTime() : 0;
    return bTs - aTs;
  })
));

const startIndex = computed(() => {
  if (totalSessions.value <= 0) return 0;
  return ((currentPage.value - 1) * itemsPerPage) + 1;
});

const endIndex = computed(() => {
  if (totalSessions.value <= 0) return 0;
  return Math.min(totalSessions.value, currentPage.value * itemsPerPage);
});

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
};

const getDeviceIcon = (deviceType) => {
  const type = String(deviceType || '').toLowerCase();
  if (type === 'mobile') return DevicePhoneMobileIcon;
  if (type === 'tablet') return DeviceTabletIcon;
  return ComputerDesktopIcon;
};

const getIconClass = (deviceType) => {
  const type = String(deviceType || '').toLowerCase();
  if (type === 'mobile') return 'text-purple-500';
  if (type === 'tablet') return 'text-teal-500';
  return 'text-blue-500';
};

watch(() => props.memberId, () => {
  currentPage.value = 1;
  fetchSessions();
});

watch(currentPage, () => {
  fetchSessions();
});

onMounted(() => {
  fetchSessions();
});
</script>

<style scoped>
</style>
